import PropTypes from "prop-types";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import get from "lodash/get";

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { InputGroupAddon } from "@/components/ui/input-group";

import { REQUIRED_MARK } from "./fieldClasses";

const FormCombobox = ({
  name,
  label,
  required,
  options,
  placeholder = "Select…",
  clearable,
  disabled,
  id,
  onValueChange,
  renderOption,
  renderValue,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const inputId = id ?? name;
  const { contains } = ComboboxPrimitive.useFilter();

  // base-ui matches selection by reference and reads display/filter text off a
  // `{ value, label }` item, so keep one stable item per option and resolve the
  // RHF scalar back to it through the same set.
  const items = useMemo(
    () => options.map(o => ({ value: o.value, label: o.text, option: o })),
    [options]
  );
  const byValue = useMemo(
    () => new Map(items.map(it => [it.value, it])),
    [items]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selectedItem = byValue.get(field.value) ?? null;
        const showsValueNode = Boolean(renderValue && selectedItem);
        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={inputId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <Combobox
              items={items}
              value={selectedItem}
              onValueChange={next => {
                const resolved = next ? next.value : null;
                field.onChange(resolved);
                onValueChange?.(resolved);
              }}
              // base-ui writes this label into the input on selection. With
              // renderValue the selection shows as a node beside an empty
              // input, so the label is blanked and the filter reads it directly.
              itemToStringLabel={it => (renderValue ? "" : (it?.label ?? ""))}
              filter={
                renderValue
                  ? (item, query) => contains(item, query, it => it.label)
                  : undefined
              }
              disabled={disabled}
            >
              <ComboboxInput
                id={inputId}
                placeholder={showsValueNode ? "" : placeholder}
                showClear={clearable}
                disabled={disabled}
                required={required}
                aria-invalid={error ? true : undefined}
                onBlur={field.onBlur}
              >
                {showsValueNode && (
                  <InputGroupAddon align="inline-start">
                    {renderValue(selectedItem.option)}
                  </InputGroupAddon>
                )}
              </ComboboxInput>
              <ComboboxContent>
                <ComboboxEmpty>No matches</ComboboxEmpty>
                <ComboboxList>
                  {item => (
                    <ComboboxItem key={item.value} value={item}>
                      {renderOption ? renderOption(item.option) : item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormCombobox.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  onValueChange: PropTypes.func,
  renderOption: PropTypes.func,
  renderValue: PropTypes.func,
};

export default FormCombobox;
